import {
  ElementType,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import moment from 'moment';

import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import { accent, font, neutral, radius, shadow, text } from 'styles/revampTokens';

import {
  BlockNavigationDropdownItem,
  BlockNavigationClickOptions,
} from './BlockNavigationDropdown';

export type GenericDropdownVariant = 'dashboard' | 'dimensions' | 'data';

interface GenericNavigationDropdownProperties {
  items: BlockNavigationDropdownItem[];
  variant: GenericDropdownVariant;
  onItemClick: (item: BlockNavigationDropdownItem, options?: BlockNavigationClickOptions) => void;
}

const DropdownBox = Box as ElementType;
const DropdownFlex = Flex as ElementType;

const AVATAR_TINTS = [
  { bg: '#F1ECFE', fg: '#7C4EE0' },
  { bg: '#E7F0FF', fg: '#3B74E8' },
  { bg: '#E3F6F0', fg: '#0E9E7E' },
  { bg: '#FFF2E0', fg: '#D9850B' },
  { bg: '#FDEBF1', fg: '#D14D86' },
  { bg: '#ECEEFF', fg: '#5B5FE0' },
] as const;

const hashIndex = (value: string, modulo: number): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + (value.codePointAt(index) ?? 0)) % 100_000;
  }
  return hash % modulo;
};

const initialsOf = (title: string): string =>
  title
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase() || '•';

const isModifiedClick = (event: ReactMouseEvent) =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

const compactAgo = (value?: string): string | null => {
  if (!value) return null;
  const then = moment.utc(value);
  if (!then.isValid()) return null;
  const now = moment.utc();
  const mins = now.diff(then, 'minutes');
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = now.diff(then, 'hours');
  if (hours < 24) return `${hours}h ago`;
  const days = now.diff(then, 'days');
  if (days < 7) return `${days}d ago`;
  const weeks = now.diff(then, 'weeks');
  if (weeks < 5) return `${weeks}w ago`;
  const months = now.diff(then, 'months');
  if (months < 12) return `${months}mo ago`;
  return `${now.diff(then, 'years')}y ago`;
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const PageIcon = ({ color = text.muted, size = 14 }: { color?: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect x='1.5' y='1.5' width='13' height='13' rx='2' stroke={color} strokeWidth='1.2' />
    <path
      d='M4.5 5.5h7M4.5 8h5M4.5 10.5h3.5'
      stroke={color}
      strokeWidth='1.1'
      strokeLinecap='round'
    />
  </svg>
);

const CubeIcon = ({ color = text.muted, size = 14 }: { color?: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M8 1.5L14 4.5V11.5L8 14.5L2 11.5V4.5L8 1.5Z'
      stroke={color}
      strokeWidth='1.1'
      strokeLinejoin='round'
    />
    <path
      d='M8 1.5V14.5M2 4.5L8 7.5L14 4.5'
      stroke={color}
      strokeWidth='1.1'
      strokeLinejoin='round'
    />
  </svg>
);

const FileIcon = ({ color = text.muted, size = 14 }: { color?: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M3.5 2C3.5 1.72 3.72 1.5 4 1.5H9L12.5 5V14C12.5 14.28 12.28 14.5 12 14.5H4C3.72 14.5 3.5 14.28 3.5 14V2Z'
      stroke={color}
      strokeWidth='1.1'
      strokeLinejoin='round'
    />
    <path d='M9 1.5V5H12.5' stroke={color} strokeWidth='1.1' strokeLinejoin='round' />
    <path d='M5.5 8.5h5M5.5 10.5h3' stroke={color} strokeWidth='1' strokeLinecap='round' />
  </svg>
);

const ArrowIcon = ({ color }: { color: string }) => (
  <svg width='15' height='15' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M11.6667 5.0083L10.4917 6.1833L13.4667 9.16663H3.33334V10.8333H13.4667L10.4917 13.8166L11.6667 14.9916L16.6667 9.99996L11.6667 5.0083Z'
      fill={color}
    />
  </svg>
);

const ClockIcon = ({ color = text.muted }: { color?: string }) => (
  <svg width='13' height='13' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <circle cx='7' cy='7' r='5.6' stroke={color} strokeWidth='1.1' />
    <path d='M7 4V7L9 8.2' stroke={color} strokeWidth='1.1' strokeLinecap='round' />
  </svg>
);

const ListIcon = ({ color = text.muted }: { color?: string }) => (
  <svg width='13' height='13' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M2 3.5h10M2 7h10M2 10.5h6' stroke={color} strokeWidth='1.1' strokeLinecap='round' />
  </svg>
);

const LayersIcon = ({ color = text.muted }: { color?: string }) => (
  <svg width='13' height='13' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M1.5 7L7 10L12.5 7M1.5 4L7 1L12.5 4L7 7L1.5 4Z'
      stroke={color}
      strokeWidth='1.05'
      strokeLinejoin='round'
    />
    <path d='M1.5 10L7 13L12.5 10' stroke={color} strokeWidth='1.05' strokeLinejoin='round' />
  </svg>
);

// ─── Variant helpers ──────────────────────────────────────────────────────────

const getVariantLabel = (variant: GenericDropdownVariant): string => {
  if (variant === 'dashboard') return 'pages';
  if (variant === 'dimensions') return 'dimensions';
  return 'sources';
};

const getGoToLabel = (variant: GenericDropdownVariant): string => {
  if (variant === 'dashboard') return 'Go to page';
  if (variant === 'dimensions') return 'Go to dimension';
  return 'Open source';
};

const VariantIcon = ({
  variant,
  color,
  size,
}: {
  variant: GenericDropdownVariant;
  color?: string;
  size?: number;
}) => {
  if (variant === 'dashboard') return <PageIcon color={color} size={size} />;
  if (variant === 'dimensions') return <CubeIcon color={color} size={size} />;
  return <FileIcon color={color} size={size} />;
};

const getRowSubtitle = (
  item: BlockNavigationDropdownItem,
  variant: GenericDropdownVariant,
): string | null => {
  const { source } = item;
  if (!source) return null;
  if (variant === 'dashboard') {
    return source.position != null ? `Page ${(source.position as number) + 1}` : null;
  }
  if (variant === 'dimensions') {
    const count: number | null =
      source.item_count ?? (source.items as any[] | undefined)?.length ?? null;
    return count != null ? `${count} ${count === 1 ? 'item' : 'items'}` : null;
  }
  // data
  const fileType: string =
    source.file_type ??
    ((source.file_name as string | undefined)?.toLowerCase().endsWith('.xlsx') ? 'Excel' : 'CSV');
  return fileType;
};

// ─── Detail panel content per variant ────────────────────────────────────────

interface DetailMeta {
  icon: ReactNode;
  label: string;
  value: string;
}

const getDetailMeta = (
  item: BlockNavigationDropdownItem,
  variant: GenericDropdownVariant,
): DetailMeta[] => {
  const { source } = item;
  if (!source) return [];

  if (variant === 'dashboard') {
    const metas: DetailMeta[] = [];
    if (source.position != null) {
      metas.push({
        icon: <ListIcon />,
        label: 'Position',
        value: `Page ${(source.position as number) + 1}`,
      });
    }
    if (source.blocks_count != null || source.block_count != null) {
      const count: number = source.blocks_count ?? source.block_count;
      metas.push({
        icon: <LayersIcon />,
        label: 'Blocks',
        value: `${count} ${count === 1 ? 'block' : 'blocks'}`,
      });
    }
    const updated = compactAgo(source.updated_at ?? source.last_modified_at);
    if (updated) {
      metas.push({ icon: <ClockIcon />, label: 'Updated', value: updated });
    }
    return metas;
  }

  if (variant === 'dimensions') {
    const metas: DetailMeta[] = [];
    const count: number | null =
      source.item_count ?? (source.items as any[] | undefined)?.length ?? null;
    if (count != null) {
      metas.push({
        icon: <ListIcon />,
        label: 'Items',
        value: `${count} ${count === 1 ? 'item' : 'items'}`,
      });
    }
    if (source.type) {
      metas.push({ icon: <CubeIcon />, label: 'Type', value: String(source.type) });
    }
    const updated = compactAgo(source.updated_at ?? source.last_modified_at);
    if (updated) {
      metas.push({ icon: <ClockIcon />, label: 'Updated', value: updated });
    }
    return metas;
  }

  // data
  const metas: DetailMeta[] = [];
  const fileType: string =
    source.file_type ??
    ((source.file_name as string | undefined)?.toLowerCase().endsWith('.xlsx') ? 'Excel' : 'CSV');
  metas.push({ icon: <FileIcon />, label: 'Type', value: fileType.toUpperCase() });

  const versions: number | null =
    (source.versions as any[] | undefined)?.length ?? source.version_count ?? null;
  if (versions != null) {
    metas.push({
      icon: <LayersIcon />,
      label: 'Versions',
      value: `${versions} ${versions === 1 ? 'version' : 'versions'}`,
    });
  }

  const updated = compactAgo(source.date_created ?? source.updated_at ?? source.last_modified_at);
  if (updated) {
    metas.push({ icon: <ClockIcon />, label: 'Last upload', value: updated });
  }
  return metas;
};

// ─── Component ────────────────────────────────────────────────────────────────

const GenericNavigationDropdown = ({
  items,
  variant,
  onItemClick,
}: GenericNavigationDropdownProperties): ReactElement => {
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState<string | number | null>(null);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? items.filter((item) => item.title.toLowerCase().includes(needle)) : items;
  }, [items, query]);

  const activeItem = filtered.find((item) => item.id === activeId) ?? filtered[0] ?? null;
  const activeTint = activeItem
    ? AVATAR_TINTS[hashIndex(activeItem.title, AVATAR_TINTS.length)]
    : AVATAR_TINTS[0];
  const activeMeta = activeItem ? getDetailMeta(activeItem, variant) : [];

  const renderRow = (item: BlockNavigationDropdownItem): ReactNode => {
    const tint = AVATAR_TINTS[hashIndex(item.title, AVATAR_TINTS.length)];
    const subtitle = getRowSubtitle(item, variant);
    const isActive = activeItem?.id === item.id;

    return (
      <DropdownFlex
        key={item.id}
        alignItems='center'
        gap='10px'
        px='10px'
        py='7px'
        cursor='pointer'
        borderRadius={radius.control}
        bg={isActive ? neutral.surfaceHover : 'transparent'}
        transition='background 0.1s ease'
        _hover={{ bg: neutral.surfaceHover }}
        onMouseEnter={() => setActiveId(item.id)}
        onClick={(event: ReactMouseEvent) => {
          if (isModifiedClick(event)) return;
          event.stopPropagation();
          onItemClick(item);
        }}
        onContextMenu={(event: ReactMouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          onItemClick(item, { openInNewTab: true });
        }}
      >
        <DropdownFlex
          w='30px'
          h='30px'
          flexShrink={0}
          alignItems='center'
          justifyContent='center'
          borderRadius={radius.control}
          bg={tint.bg}
          color={tint.fg}
          fontFamily={font.body}
          fontSize='11px'
          fontWeight='700'
        >
          {initialsOf(item.title)}
        </DropdownFlex>
        <DropdownFlex direction='column' flex='1 1 0' minW={0} gap='1px'>
          <DropdownBox
            color={text.primary}
            fontFamily={font.body}
            fontSize='13px'
            fontWeight='500'
            lineHeight='17px'
            noOfLines={1}
          >
            {item.title}
          </DropdownBox>
          {subtitle && (
            <DropdownBox color={text.secondary} fontSize='11px' lineHeight='14px' noOfLines={1}>
              {subtitle}
            </DropdownBox>
          )}
        </DropdownFlex>
        <DropdownFlex
          flexShrink={0}
          color={isActive ? accent.solid : text.muted}
          transition='color 0.1s ease'
        >
          <VariantIcon variant={variant} color={isActive ? accent.solid : text.muted} />
        </DropdownFlex>
      </DropdownFlex>
    );
  };

  return (
    <DropdownFlex
      w='560px'
      maxW='96vw'
      h='380px'
      maxH='calc(100vh - 96px)'
      bg={neutral.surface}
      border={`1px solid ${neutral.border}`}
      boxShadow={shadow.overlay}
      borderRadius={radius.overlay}
      overflow='hidden'
      flexDirection='column'
      onClick={(event: ReactMouseEvent) => event.stopPropagation()}
    >
      {/* Search bar */}
      <DropdownFlex
        alignItems='center'
        gap='8px'
        px='14px'
        h='46px'
        flexShrink={0}
        borderBottom={`1px solid ${neutral.border}`}
      >
        <FigmaSearchIcon width={13} height={13} color={text.muted} />
        <DropdownBox
          as='input'
          type='text'
          value={query}
          placeholder={`Search ${getVariantLabel(variant)}`}
          onChange={(event: any) => setQuery(event.target.value)}
          flex='1'
          minW={0}
          bg='transparent'
          border='none'
          outline='none'
          color={text.primary}
          fontFamily={font.body}
          fontSize='13px'
          sx={{ '::placeholder': { color: text.muted } }}
        />
        {filtered.length > 0 && (
          <DropdownBox
            px='7px'
            h='20px'
            borderRadius='999px'
            bg={neutral.surfaceHover}
            color={text.muted}
            fontSize='11px'
            fontFamily={font.body}
            fontWeight='600'
            flexShrink={0}
            display='flex'
            alignItems='center'
          >
            {filtered.length}
          </DropdownBox>
        )}
      </DropdownFlex>

      {/* Body: list + detail */}
      <DropdownFlex flex='1 1 auto' minH={0}>
        {/* Left: scrollable list */}
        <DropdownFlex
          direction='column'
          flex='1 1 0'
          minW={0}
          overflowY='auto'
          p='6px'
          borderRight={`1px solid ${neutral.border}`}
        >
          {filtered.length > 0 ? (
            filtered.map((item) => renderRow(item))
          ) : (
            <DropdownBox
              px='12px'
              py='28px'
              textAlign='center'
              color={text.secondary}
              fontSize='13px'
              fontFamily={font.body}
            >
              {query.trim()
                ? `No ${getVariantLabel(variant)} match your search`
                : `No ${getVariantLabel(variant)} yet`}
            </DropdownBox>
          )}
        </DropdownFlex>

        {/* Right: detail preview */}
        <DropdownFlex
          direction='column'
          w='212px'
          flexShrink={0}
          p='16px'
          gap='12px'
          bg={neutral.surfaceMuted}
        >
          {activeItem ? (
            <>
              {/* Avatar + name */}
              <DropdownFlex direction='column' gap='10px'>
                <DropdownFlex
                  w='44px'
                  h='44px'
                  alignItems='center'
                  justifyContent='center'
                  borderRadius={radius.surface}
                  bg={activeTint.bg}
                  color={activeTint.fg}
                  fontFamily={font.body}
                  fontSize='16px'
                  fontWeight='700'
                >
                  {initialsOf(activeItem.title)}
                </DropdownFlex>
                <DropdownFlex direction='column' gap='3px'>
                  <DropdownBox
                    color={text.primary}
                    fontFamily={font.display}
                    fontSize='14px'
                    fontWeight='600'
                    lineHeight='19px'
                    noOfLines={2}
                  >
                    {activeItem.title}
                  </DropdownBox>
                  {/* Type badge */}
                  <DropdownFlex alignItems='center' gap='5px'>
                    <DropdownBox color={text.muted}>
                      <VariantIcon variant={variant} color={text.muted} size={12} />
                    </DropdownBox>
                    <DropdownBox color={text.secondary} fontSize='11px' fontFamily={font.body}>
                      {variant === 'dashboard'
                        ? 'Dashboard page'
                        : variant === 'dimensions'
                        ? 'Dimension'
                        : 'Data source'}
                    </DropdownBox>
                  </DropdownFlex>
                </DropdownFlex>
              </DropdownFlex>

              {/* Description */}
              {activeItem.source?.description && (
                <DropdownBox
                  color={text.secondary}
                  fontFamily={font.body}
                  fontSize='12px'
                  lineHeight='17px'
                  noOfLines={3}
                >
                  {activeItem.source.description}
                </DropdownBox>
              )}

              {/* Meta rows */}
              {activeMeta.length > 0 && (
                <DropdownFlex direction='column' gap='7px'>
                  {activeMeta.map((meta) => (
                    <DropdownFlex key={meta.label} alignItems='center' gap='8px'>
                      <DropdownBox color={text.muted} flexShrink={0}>
                        {meta.icon}
                      </DropdownBox>
                      <DropdownBox color={text.secondary} fontSize='12px' fontFamily={font.body}>
                        {meta.value}
                      </DropdownBox>
                    </DropdownFlex>
                  ))}
                </DropdownFlex>
              )}

              {/* CTA */}
              <DropdownFlex mt='auto'>
                <DropdownFlex
                  as='button'
                  type='button'
                  alignItems='center'
                  justifyContent='center'
                  gap='6px'
                  h='36px'
                  w='100%'
                  borderRadius={radius.control}
                  bg={accent.solid}
                  color='#FFFFFF'
                  fontFamily={font.body}
                  fontSize='13px'
                  fontWeight='600'
                  transition='background 0.1s ease'
                  _hover={{ bg: accent.hover }}
                  onClick={() => onItemClick(activeItem)}
                >
                  {getGoToLabel(variant)}
                  <ArrowIcon color='#FFFFFF' />
                </DropdownFlex>
              </DropdownFlex>
            </>
          ) : (
            <DropdownBox color={text.secondary} fontSize='13px' m='auto' textAlign='center'>
              Hover to preview
            </DropdownBox>
          )}
        </DropdownFlex>
      </DropdownFlex>
    </DropdownFlex>
  );
};

export default GenericNavigationDropdown;
